import { groupBy } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import { WizardStepProps } from 'shared/types'
import styled from 'styled-components'
import { lsSubmissionSummaryTableSchema } from '../schemas'

const StyledActionableSection = styled.div`
  display: flex;
  flex-direction: column;

  & > :first-child {
    margin-bottom: 0.25rem;
  }
`

type SummaryData = {
  market: string
  brand: string
  location: string
  date: string
}

type TableItem = Pick<SummaryData, 'market' | 'brand' | 'location'> & {
  dateRange: string
  status: React.ReactNode
}

type StepState = {
  summary: SummaryData[]
}

export type Props = WizardStepProps<StepState>

export default function LSSubmissionSummary({ state }: Props) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [items, setItems] = useState<TableItem[]>([])

  const tableColumns = lsSubmissionSummaryTableSchema

  const toTableItem = (summary: SummaryData[]) => {
    const groupedByLocation = groupBy(
      summary,
      ({ market, brand, location }) => `${market}-|-${brand}-|-${location}`,
    )

    return Object.entries(groupedByLocation).map(([key, value]) => {
      const [market, brand, location] = key.split('-|-')

      const dates = value.map(({ date }) => DateTime.fromISO(date))
      const minDate = DateTime.min(...dates).toISODate()
      const maxDate = DateTime.max(...dates).toISODate()

      return {
        market,
        brand,
        location,
        dateRange: `${minDate} - ${maxDate}`,
        status: (
          <div style={{ display: 'inline-block' }}>
            <IWGeneralBadge
              label={t('loadScheduling.processing')}
              color="warning"
            />
          </div>
        ),
      }
    })
  }

  useEffect(() => {
    if (state?.summary?.length) {
      setItems(toTableItem(state?.summary))
      setIsLoading(false)
    }
  }, [state?.summary])

  return (
    <>
      <StyledActionableSection>
        <IWTypography
          size="lg"
          weight="medium"
          fontHue={{ color: 'grey', value: 900 }}
        >
          {t('loadScheduling.titles.summaryResultLabel')}
        </IWTypography>
      </StyledActionableSection>

      {isLoading && <IWLoading />}

      {Boolean(items?.length) && !isLoading && (
        <IWGenericTable
          border
          items={items}
          columns={tableColumns}
          showColumnSettings
        />
      )}
    </>
  )
}
